import axios from "axios";
import moment from "moment";
import { User } from "../../models/User";
import { UserAnalytics } from "../../models/UserAnalytics";
import { SignInAttribute, SignInError } from "../../pages/auth/signIn";
import { SignUpAttribute, SignUpError } from "../../pages/auth/signUp";

let unsubscribeAuthListener: any = null;
let unsubscribeUserListener: any = null;

export const getAuthListener = (dispatch: any, firebase: any) => {
  if (!unsubscribeAuthListener) {
    unsubscribeAuthListener = firebase
      .auth()
      .onAuthStateChanged(function (user: any) {
        if (user) {
          dispatch({
            type: "UPDATE_USER_AUTH",
            payload: {
              userAuth: user,
            },
          });
          getUserInfoListener(dispatch, firebase);
        } else {
          dispatch({
            type: "UPDATE_USER_AUTH",
            payload: {
              userAuth: null,
            },
          });
          removeUserInfoListener();
        }
      });
  }
};

export const removeAuthListener = () => {
  if (unsubscribeAuthListener) {
    unsubscribeAuthListener();
    unsubscribeAuthListener = null;
  }
  removeUserInfoListener();
};

export const getUserInfoListener = async (dispatch: any, firebase: any) => {
  try {
    if (firebase.auth().currentUser?.uid) {
      unsubscribeUserListener = firebase
        .firestore()
        .collection("users")
        .doc(firebase.auth().currentUser?.uid)
        .onSnapshot(async (doc: any) => {
          if (doc) {
            dispatch({
              type: "UPDATE_USER",
              payload: {
                user: doc.data(),
              },
            });
          }
        });
    }
  } catch (err) {}
};

const removeUserInfoListener = () => {
  if (unsubscribeUserListener) {
    unsubscribeUserListener();
    unsubscribeUserListener = null;
  }
};

export const checkUserExists = async (firebase: any) => {
  try {
    if (firebase.auth().currentUser?.uid) {
      const userQuery = await firebase
        .firestore()
        .collection("users")
        .doc(firebase.auth().currentUser?.uid)
        .get();
      if (userQuery.exists) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  } catch (err) {
    return false;
  }
};

export const signIn = async (
  credentials: {
    email: string;
    password: string;
  },
  firebase: any
) => {
  try {
    await firebase
      .auth()
      .signInWithEmailAndPassword(credentials.email, credentials.password);
  } catch (err: any) {
    return err.message;
  }
};

export const signInWithGoogle = async (firebase: any, provider: any) => {
  try {
    await firebase.auth().signInWithPopup(provider);
  } catch (err) {
    return err.message;
  }
};

export const signOut = async (firebase: any) => {
  try {
    await firebase.auth().signOut();
  } catch (err) {}
};

export const verifyOobCode = async (oobCode: string, firebase: any) => {
  const result = await firebase.auth().verifyPasswordResetCode(oobCode);
  return result;
};

export const verifyEmailLink = async (oobCode: string, firebase: any) => {
  await firebase.auth().applyActionCode(oobCode);
};

export const checkEmailVerified = async (firebase: any) => {
  if (firebase.auth().currentUser) {
    return await firebase.auth().currentUser.emailVerified;
  } else {
    return false;
  }
};

export const resendVerificationEmail = async (firebase: any) => {
  if (firebase.auth().currentUser) {
    await firebase.auth().currentUser?.sendEmailVerification();
  }
};

export const resetPassword = async (email: string, firebase: any) => {
  try {
    await firebase.auth().sendPasswordResetEmail(email);
    return "";
  } catch (err: any) {
    return err.message;
  }
};

export const resetPasswordWithOob = async (
  oobCode: string,
  password: string,
  firebase: any
) => {
  await firebase.auth().confirmPasswordReset(oobCode, password);
};

const instanceOfSignUp = (
  object: any,
  member: string
): object is SignUpAttribute => {
  return member in object;
};

const instanceOfSignUpError = (
  object: any,
  member: string
): object is SignUpError => {
  return member in object;
};

export const getUserAnalytics = async (firebase: any) => {
  let userAnalytics = {
    hourVolunteered: 0,
    dayVolunteered: 0,
  };
  try {
    if (firebase.auth().currentUser?.uid) {
      let userAnalyticsQuery = await firebase
        .firestore()
        .collection("userAnalytics")
        .doc(firebase.auth().currentUser?.uid)
        .get();

      if (userAnalyticsQuery.exists) {
        const userAnalyticsData: UserAnalytics =
          userAnalyticsQuery.data() as UserAnalytics;
        userAnalytics["hourVolunteered"] =
          userAnalyticsData.totalHoursVolunteered;
        userAnalytics["dayVolunteered"] =
          userAnalyticsData.totalDaysVolunteered;
      }
    }
    return userAnalytics;
  } catch (err: any) {
    return userAnalytics;
  }
};

export const handleAuthCondition = (
  userAttributeState: SignInAttribute | SignUpAttribute,
  userAttributeError: SignInError | SignUpError,
  typeList: string[]
) => {
  typeList.map((eachType) => {
    switch (eachType) {
      case "email":
        const filter =
          /^([a-zA-Z0-9_\.\-])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,4})+$/; // eslint-disable-line
        if (!filter.test(userAttributeState.email.replace(/\s/g, ""))) {
          userAttributeError["emailError"] =
            "Please enter your email in the correct format";
        } else {
          userAttributeError["emailError"] = "";
        }
        break;
      case "password":
        if (userAttributeState.password.replace(/\s/g, "").length < 6) {
          userAttributeError["passwordError"] =
            "Please input a minimum of 6 characters";
        } else {
          userAttributeError["passwordError"] = "";
        }
        break;
      case "ic":
        if (
          instanceOfSignUp(userAttributeState, "ic") &&
          instanceOfSignUpError(userAttributeError, "icError")
        ) {
          if (userAttributeState.ic.replace(/\s/g, "").length < 6) {
            userAttributeError["icError"] =
              "Please enter your IC or passport number";
          } else {
            userAttributeError["icError"] = "";
          }
        }
        break;
      case "name":
        if (
          instanceOfSignUp(userAttributeState, "name") &&
          instanceOfSignUpError(userAttributeError, "nameError")
        ) {
          if (userAttributeState.name.replace(/\s/g, "").length <= 0) {
            userAttributeError["nameError"] = "Please enter your name";
          } else {
            userAttributeError["nameError"] = "";
          }
        }
        break;
      case "street":
        if (
          instanceOfSignUp(userAttributeState, "address") &&
          instanceOfSignUpError(userAttributeError, "addressError")
        ) {
          if (
            userAttributeState.address.street.replace(/\s/g, "").length <= 0
          ) {
            userAttributeError["addressError"]["streetError"] =
              "Please enter your address";
          } else {
            userAttributeError["addressError"]["streetError"] = "";
          }
        }
        break;
      case "poscode":
        if (
          instanceOfSignUp(userAttributeState, "address") &&
          instanceOfSignUpError(userAttributeError, "addressError")
        ) {
          if (
            userAttributeState.address.poscode.replace(/\s/g, "").length <= 0
          ) {
            userAttributeError["addressError"]["poscodeError"] =
              "Please enter your poscode";
          } else {
            userAttributeError["addressError"]["poscodeError"] = "";
          }
        }
        break;
      case "mobileNo":
        if (
          instanceOfSignUp(userAttributeState, "mobileNo") &&
          instanceOfSignUpError(userAttributeError, "mobileNoError")
        ) {
          const phoneFilter = /^(\+60)-*[0-9]{8,10}$/;
          if (!phoneFilter.test(userAttributeState.mobileNo)) {
            userAttributeError["mobileNoError"] =
              "Please enter your contact number in the correct format of +60123456789 (Example)";
          } else {
            userAttributeError["mobileNoError"] = "";
          }
        }
        break;
      case "age":
        if (
          instanceOfSignUp(userAttributeState, "age") &&
          instanceOfSignUpError(userAttributeError, "ageError")
        ) {
          if (isNaN(userAttributeState.age)) {
            userAttributeError["ageError"] = "Please enter your age";
          } else if (userAttributeState.age < 18) {
            userAttributeError["ageError"] = "You are currently under age";
          } else {
            userAttributeError["ageError"] = "";
          }
        }
        break;
      default:
        break;
    }
    return null;
  });
};

export const createAccountWithGoogle = async (
  credentials: {
    name: string;
    ic: string;
    age: number;
    street: string;
    poscode: string;
    district: string;
    state: string;
    mobileNo: string;
  },
  firebase: any
) => {
  try {
    if (firebase.auth().currentUser?.uid) {
      const userUUID = firebase.auth().currentUser?.uid ?? "";
      const userModel = {
        id: userUUID,
        createdAt: new Date(),
        ic: credentials.ic,
        name: credentials.name,
        age: credentials.age,
        address: {
          street: credentials.street,
          district: credentials.district,
          poscode: credentials.poscode,
          state: credentials.state,
        },
        email: firebase.auth().currentUser?.email ?? "",
        mobileNo: credentials.mobileNo,
        imageToken: "",
      };
      const userExist = await checkUserExists(firebase);
      if (!userExist) {
        await firebase
          .firestore()
          .collection("users")
          .doc(firebase.auth().currentUser?.uid)
          .set(userModel);

        const isProd = process.env.GATSBY_FIREBASE_ENV === "production";
        if (isProd) {
          subscribeToEngineMailer(userModel);
        }
      }
      return "";
    } else {
      return "User is not created. Unknown Error.";
    }
  } catch (err) {
    return err.message;
  }
};

export const createAccount = async (
  credentials: {
    name: string;
    ic: string;
    email: string;
    password: string;
    age: number;
    street: string;
    poscode: string;
    district: string;
    state: string;
    mobileNo: string;
  },
  firebase: any
) => {
  try {
    const createdCredentials = await firebase
      .auth()
      .createUserWithEmailAndPassword(credentials.email, credentials.password);
    if (createdCredentials.user) {
      const userUUID: string = createdCredentials.user?.uid;
      const userModel: User = {
        id: userUUID,
        createdAt: new Date(),
        ic: credentials.ic,
        name: credentials.name,
        age: credentials.age,
        address: {
          street: credentials.street,
          district: credentials.district,
          poscode: credentials.poscode,
          state: credentials.state,
        },
        email: createdCredentials.user?.email ?? "",
        mobileNo: credentials.mobileNo,
        imageToken: "",
      };
      await firebase
        .firestore()
        .collection("users")
        .doc(userUUID)
        .set(userModel);
      await createdCredentials.user.sendEmailVerification();

      const isProd = process.env.GATSBY_FIREBASE_ENV === "production";
      if (isProd) {
        subscribeToEngineMailer(userModel);
      }
      return "";
    } else return "User is not created. Unknown Error.";
  } catch (err: any) {
    return err.message;
  }
};

const subscribeToEngineMailer = async (eachUser: User) => {
  try {
    const headers = {
      APIKey: process.env.GATSBY_EMAIL_API ?? "",
    };
    await axios.post(
      "https://api.enginemailer.com/restapi/subscriber/emsubscriber/insertSubscriber",
      {
        email: eachUser.email,
        customfields: [
          {
            customfield_key: "first_name",
            customfield_value: eachUser.name,
          },
          {
            customfield_key: "mobile",
            customfield_value: eachUser.mobileNo,
          },
          {
            customfield_key: "state",
            customfield_value: eachUser.address.state,
          },
          {
            customfield_key: "created_at",
            customfield_value: moment(eachUser.createdAt).format(
              "DD-MMMM-YYYY"
            ),
          },
        ],
      },
      { headers: headers }
    );
  } catch (err) {}
};

export const updateAccount = async (
  credentials: {
    name: string;
    ic: string;
    age: number;
    street: string;
    poscode: string;
    district: string;
    state: string;
    mobileNo: string;
  },
  firebase: any
) => {
  try {
    if (firebase.auth().currentUser?.uid) {
      await firebase
        .firestore()
        .collection("users")
        .doc(firebase.auth().currentUser?.uid ?? "")
        .update({
          name: credentials.name,
          ic: credentials.ic,
          age: credentials.age,
          mobileNo: credentials.mobileNo,
          address: {
            street: credentials.street,
            district: credentials.district,
            poscode: credentials.poscode,
            state: credentials.state,
          },
        });
      return "";
    } else return "User is not updated. Unknown Error.";
  } catch (err: any) {
    return err.message;
  }
};

export const handleProfileUpdateField = async (
  type: String,
  value: any,
  firebase: any
) => {
  try {
    switch (type) {
      case "image": {
        const uploadTask = await firebase
          .storage()
          .ref("users")
          .child(firebase.auth().currentUser?.uid + "/profile.jpg")
          .put(value);
        const uploadTaskUrl: string = await uploadTask.ref.getDownloadURL();
        const urlParams = new URLSearchParams(uploadTaskUrl);
        const imageToken = urlParams.get("token");
        await firebase
          .firestore()
          .collection("users")
          .doc(firebase.auth().currentUser?.uid)
          .update({ imageToken: imageToken });
        return "";
      }
    }
  } catch (err: any) {
    return err.message;
  }
};

export const handleRemoveProfilePicture = async (firebase: any) => {
  try {
    await firebase
      .storage()
      .ref("users")
      .child(firebase.auth().currentUser?.uid + "/profile.jpg")
      .delete();
    await firebase
      .firestore()
      .collection("users")
      .doc(firebase.auth().currentUser?.uid)
      .update({ imageToken: "" });
    return "";
  } catch (err: any) {
    return err.message;
  }
};

export const updateAuthorisedNGOs = async (ngoId: string[], firebase: any) => {
  try {
    if (firebase.auth().currentUser?.uid) {
      await firebase
        .firestore()
        .collection("users")
        .doc(firebase.auth().currentUser?.uid ?? "")
        .update({
          authorisedNGOs: ngoId,
        });
      return "";
    } else return "User is not updated. Unknown Error.";
  } catch (err: any) {
    return err.message;
  }
};
